import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/soul";
import { toast } from "sonner";
import { Trash2, BookMarked } from "lucide-react";

export default function Lexicon() {
  const [words, setWords] = useState([]);
  const [openWord, setOpenWord] = useState(null);

  const load = useCallback(() => {
    api.get("/lexicon").then((r) => setWords(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const remove = async (word) => {
    try {
      await api.delete(`/lexicon/${encodeURIComponent(word)}`);
      toast.success("Removed from your lexicon.");
      load();
    } catch {
      toast.error("Could not remove.");
    }
  };

  return (
    <main data-testid="lexicon-page" className="max-w-6xl mx-auto px-6 py-16">
      <div className="max-w-3xl mb-12 fade-up">
        <p className="text-[#D4AF37] tracking-[0.3em] uppercase text-xs mb-4 flex items-center gap-2">
          <BookMarked size={14} /> Living Lexicon
        </p>
        <h1 className="serif text-4xl sm:text-6xl font-light text-[#F5F1E7]">
          Your collection of <span className="italic-serif text-[#D4AF37]">living words</span>
        </h1>
        <p className="mt-5 text-[#A8A29E] leading-relaxed text-lg">
          The words whose origins moved you, kept close. Return to them, let them keep working in you.
        </p>
      </div>

      {words.length === 0 ? (
        <div className="glass rounded-2xl p-12 text-[#A8A29E]">
          <p className="italic-serif text-xl">Your lexicon is empty.</p>
          <Link to="/words" className="mt-4 inline-block text-[#D4AF37] hover:underline">
            Explore the origins of a word →
          </Link>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 gap-6" data-testid="lexicon-list">
          {words.map((sw) => {
            const r = sw.result || {};
            const isOpen = openWord === sw.word;
            return (
              <div key={sw.id} data-testid={`lexicon-item-${sw.word}`} className="glass rounded-2xl p-7">
                <div className="flex items-start justify-between gap-3">
                  <button onClick={() => setOpenWord(isOpen ? null : sw.word)} className="text-left">
                    <h2 className="serif text-3xl text-[#F5F1E7]">{r.word || sw.word}</h2>
                    {r.language_detected && (
                      <span className="text-xs uppercase tracking-[0.2em] text-[#D4AF37]">{r.language_detected}</span>
                    )}
                  </button>
                  <button
                    data-testid={`lexicon-delete-${sw.word}`}
                    onClick={() => remove(sw.word)}
                    className="text-[#A8A29E] hover:text-[#C1272D] transition-colors duration-300"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
                {r.inner_meaning && (
                  <p className="mt-3 italic-serif text-lg text-[#F5F1E7] leading-relaxed">{r.inner_meaning}</p>
                )}
                {isOpen && (
                  <div className="mt-4 border-t border-white/5 pt-4 space-y-3">
                    {r.etymology && <p className="text-[#A8A29E] leading-relaxed text-sm">{r.etymology}</p>}
                    {Array.isArray(r.across_languages) && (
                      <div className="space-y-2">
                        {r.across_languages.map((l, i) => (
                          <div key={i} className="flex items-baseline justify-between gap-3 text-sm">
                            <span className="text-[#F5F1E7] serif text-lg">{l.word}</span>
                            <span className="text-[#A8A29E]">{l.language} — {l.meaning}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
                <button
                  onClick={() => setOpenWord(isOpen ? null : sw.word)}
                  className="mt-4 text-sm text-[#A8A29E] hover:text-[#D4AF37] transition-colors duration-300"
                >
                  {isOpen ? "Show less" : "Show origins & languages"}
                </button>
              </div>
            );
          })}
        </div>
      )}
    </main>
  );
}
